import assertDefined from '@/utils/assertDefined'
import { pluralize, wordWithArticle } from '@/utils/helpers'

import { DELETED_USER_FIRST_NAME } from '@/queries/constants'
import { EVENT_NOTIFICATION_TITLE } from '@/client/services/progression'
import {
  CHANNEL_TYPE_TO_HEADING,
  CHANNEL_TYPE_TO_POST_TYPE,
} from '@/client/services/worlds/constants'

import getWorldLevelTitle from '../getWorldLevelTitle'
import type { NotificationBodyArgs, NotificationBodyDetails, NotificationType } from './types'

const getNotificationLocation = ({
  worldTitle,
  channelTitle,
}: {
  worldTitle?: string
  channelTitle?: string
}) => worldTitle ?? channelTitle ?? 'Schoolhouse'

const NOTIFICATION_TYPE_TO_BODY_DETAILS: {
  [T in NotificationType]: (notification: NotificationBodyArgs<T>) => NotificationBodyDetails[]
} = {
  assessment_feedback_received: notification => {
    const { id, relatedUser, assessment } = notification
    assertDefined(assessment, `Assessment not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      { text: ' just gave you feedback on your ' },
      { text: assessment.title, emphasized: true },
      { text: ' assessment!' },
    ]
  },
  certification_request_approved: notification => {
    const { topic, course, id } = notification
    assertDefined(topic, `Topic not found for Notification#${id}`)
    assertDefined(course, `Course not found for Notification#${id}`)

    return [
      { text: 'Your certification request for ' },
      { text: `${course.name}: ${topic.name}`, emphasized: true },
      { text: ' was approved!' },
    ]
  },
  certification_request_rejected: notification => {
    const { topic, course, id } = notification
    assertDefined(topic, `Topic not found for Notification#${id}`)
    assertDefined(course, `Course not found for Notification#${id}`)

    return [
      { text: 'Your certification request for ' },
      { text: `${course.name}: ${topic.name}`, emphasized: true },
      { text: ' was rejected.' },
    ]
  },
  post_reply_commenter: notification => {
    const { payload, relatedUser, world, post, id } = notification
    const { additionalUserCount } = payload

    assertDefined(post, `Post not found for Notification#${id}`)
    return [
      {
        text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME,
        emphasized: true,
      },
      {
        text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
      },
      {
        text: `also replied to ${post.user?.displayName ?? DELETED_USER_FIRST_NAME}'s ${
          CHANNEL_TYPE_TO_HEADING[post.channel?.type ?? 'discussion']
        } in `,
      },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  post_reply_original_poster: notification => {
    const { payload, relatedUser, world, post, id } = notification
    const { additionalUserCount } = payload

    assertDefined(post, `Post not found for Notification#${id}`)

    return [
      {
        text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME,
        emphasized: true,
      },
      {
        text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
      },
      {
        text: `replied to your ${CHANNEL_TYPE_TO_HEADING[post.channel?.type ?? 'discussion']} in `,
      },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  // Most of the other post notifications support a null world, but live help will be ported over separately
  live_help_response: notification => {
    const { world, relatedUser, id } = notification

    assertDefined(world, `World not found for Notification#${id}`)

    return [
      {
        text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME,
        emphasized: true,
      },
      { text: ' opened a Zoom room to help answer your question in ' },
      {
        text: world.title,
        emphasized: true,
      },
    ]
  },

  parent_consent_completed: _ => [{ text: 'Your parent has completed the consent form!' }],

  new_post: notification => {
    const { world, relatedUser, id, post } = notification

    assertDefined(post, `Post not found for Notification#${id}`)

    return [
      {
        text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME,
        emphasized: true,
      },
      { text: ' posted an announcement in ' },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  post_feedback_received: notification => {
    const { relatedUser, world, post, id } = notification

    assertDefined(post, `Post not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: ` gave you feedback on their ${
          CHANNEL_TYPE_TO_POST_TYPE[post.channel?.type ?? 'discussion']
        } in `,
      },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  session_registration_created: notification => {
    const { payload, relatedUser, id, session } = notification
    const { additionalUserCount } = payload

    assertDefined(session, `Session not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
      },
      { text: 'registered for your ' },
      { text: session.title, emphasized: true },
      { text: ' session.' },
    ]
  },

  post_mention: notification => {
    const { relatedUser, world, post, id } = notification

    assertDefined(post, `Post not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: ` mentioned you in ${wordWithArticle(
          CHANNEL_TYPE_TO_POST_TYPE[post.channel?.type ?? 'discussion']
        )} in `,
      },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  series_registration_created: notification => {
    const { payload, relatedUser, id, series } = notification
    const { additionalUserCount } = payload

    assertDefined(series, `Session not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
      },
      { text: 'registered for your ' },
      { text: series.title, emphasized: true },
      { text: ' series.' },
    ]
  },

  post_event_mention: notification => {
    const { relatedUser, world, post, id } = notification

    assertDefined(post, `Post not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: ` mentioned you in a comment on ${post.user?.displayName ?? DELETED_USER_FIRST_NAME}'s ${
          CHANNEL_TYPE_TO_POST_TYPE[post.channel?.type ?? 'discussion']
        } in `,
      },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  user_level_up: notification => {
    const { payload, world, id } = notification
    const { level } = payload

    assertDefined(world, `World not found for Notification#${id}`)

    return [
      { text: `Congratulations! You've been promoted to a ` },
      { text: getWorldLevelTitle({ level, worldSlug: world.slug }), emphasized: true },
      { text: ' in ' },
      { text: world.title, emphasized: true },
    ]
  },

  tutor_audit_received: notification => {
    const { relatedUser } = notification

    return [
      { text: 'You received a peer review from ' },
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
    ]
  },

  tutor_followed: notification => {
    const { relatedUser, payload } = notification
    const { additionalUserCount } = payload

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
      },
      { text: 'started following you!' },
    ]
  },

  post_reaction: notification => {
    const { payload, relatedUser, world, post, id } = notification
    const { additionalUserCount } = payload

    assertDefined(post, `Post not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
      },
      {
        text: `reacted to your ${
          CHANNEL_TYPE_TO_POST_TYPE[post.channel?.type ?? 'discussion']
        } in `,
      },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  post_event_reaction: notification => {
    const { payload, relatedUser, world, post, id } = notification
    const { additionalUserCount } = payload

    assertDefined(post, `Post not found for Notification#${id}`)

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      {
        text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
      },
      {
        text: `reacted to your comment on ${post.user?.displayName ?? DELETED_USER_FIRST_NAME}'s ${
          CHANNEL_TYPE_TO_POST_TYPE[post.channel?.type ?? 'discussion']
        } in `,
      },
      {
        text: getNotificationLocation({
          worldTitle: world?.title,
          channelTitle: post.channel?.title,
        }),
        emphasized: true,
      },
    ]
  },

  progression_points_earned: notification => {
    const { userEvent, id, payload } = notification
    assertDefined(userEvent, `No UserEvent found for Notification#${id}`)

    const { type: eventType, payload: eventPayload, points: eventPoints } = userEvent
    const note = 'note' in eventPayload ? eventPayload.note : null

    const points = payload.points ?? eventPoints

    if (note != null) {
      return [
        { text: `You earned ${pluralize(points, 'point')} this week. `, emphasized: true },
        { text: note },
      ]
    }

    return [
      { text: `You earned ${pluralize(points, 'point')} this week`, emphasized: true },
      { text: ` for ${EVENT_NOTIFICATION_TITLE[eventType] ?? ''}` },
    ]
  },

  session_comment: notification => {
    const { id, user, session, relatedUser, payload } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    const { additionalUserCount } = payload
    const { title } = session

    const mainTutor = session.hosts.find(host => host.role === 'main') ?? session.hosts[0]

    const isCurrentUserTutor = session.userId === user.id
    const isRelatedUserTutor = relatedUser?.id === mainTutor.id

    if (isCurrentUserTutor) {
      return [
        { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
        {
          text: additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')}` : '',
        },
        { text: ` commented on your ${title} session` },
      ]
    }

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      { text: isRelatedUserTutor ? ' (tutor) ' : ' ' },
      {
        text: additionalUserCount !== 0 ? `and ${pluralize(additionalUserCount, 'other')}` : '',
      },
      { text: ` also commented on ${title} with ${mainTutor.displayName}` },
    ]
  },

  series_comment: notification => {
    const { id, user, series, relatedUser, payload } = notification

    assertDefined(series, `Series not found for Notification#${id}`)

    const { additionalUserCount } = payload
    const { title } = series

    const mainTutor = series.hosts.find(host => host.role === 'main') ?? series.hosts[0]

    const isCurrentUserTutor = user.id === mainTutor.id
    const isRelatedUserTutor = relatedUser?.id === mainTutor.id

    if (isCurrentUserTutor) {
      return [
        { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
        {
          text:
            additionalUserCount !== 0 ? ` and ${pluralize(additionalUserCount, 'other')} ` : ' ',
        },
        { text: `commented on your series: ${title}` },
      ]
    }

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      { text: isRelatedUserTutor ? ' (tutor) ' : ' ' },
      {
        text: additionalUserCount !== 0 ? `and ${pluralize(additionalUserCount, 'other')}` : '',
      },
      { text: ` also commented on ${title} with ${mainTutor.displayName}` },
    ]
  },

  audit_reply_received: notification => {
    const { relatedUser } = notification

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      { text: ' just replied to your peer review!' },
    ]
  },

  session_feedback_received: notification => {
    const { id, session, payload, relatedUser } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    const additionalUserCount =
      relatedUser == null ? payload.additionalUserCount : payload.additionalUserCount + 1

    return [
      { text: pluralize(additionalUserCount, 'learner'), emphasized: true },
      { text: ` gave you feedback on your ${session.title} session` },
    ]
  },

  session_feedback_automated_ready: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return [
      { text: 'Automated feedback for your ' },
      { text: session.title, emphasized: true },
      { text: ' session is ready' },
    ]
  },

  session_feedback_updated: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return [
      { text: 'Feedback for your ' },
      { text: session.title, emphasized: true },
      { text: ' session has been updated' },
    ]
  },

  volunteer_team_joined: notification => {
    const { teamName, roleName } = notification.payload

    return [
      { text: 'Congratulations, you are now a ' },
      { text: roleName, emphasized: true },
      { text: ' on the ' },
      { text: teamName, emphasized: true },
      { text: ' team! Head to the team page to view team resources.' },
    ]
  },

  volunteer_team_left: notification => {
    const { teamName, roleName } = notification.payload

    return [
      { text: 'You have been removed as a ' },
      { text: roleName, emphasized: true },
      { text: ' on the ' },
      { text: teamName, emphasized: true },
      { text: ' team.' },
    ]
  },

  volunteer_offer_extended: notification => {
    const { roleName, teamName } = notification.payload

    return [
      { text: 'Congratulations! You have been offered a position on the ' },
      { text: teamName, emphasized: true },
      { text: ' team as a ' },
      { text: roleName, emphasized: true },
      { text: '. ' },
      { text: 'Go to your application to accept your offer!', emphasized: true },
    ]
  },

  volunteer_offer_accepted: notification => {
    const { payload, relatedUser } = notification

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      { text: ' has accepted their offer to join the ' },
      { text: payload.teamName, emphasized: true },
      { text: ' team as a ' },
      { text: payload.roleName, emphasized: true },
    ]
  },

  volunteer_offer_declined: notification => {
    const { payload, relatedUser } = notification

    return [
      { text: relatedUser?.displayName ?? DELETED_USER_FIRST_NAME, emphasized: true },
      { text: ' has declined their offer to join the ' },
      { text: payload.teamName, emphasized: true },
      { text: ' team as a ' },
      { text: payload.roleName, emphasized: true },
    ]
  },

  volunteer_application_rejected: notification => [
    { text: 'Sorry, you were not selected to join the ' },
    { text: notification.payload.teamName, emphasized: true },
    { text: ' team as a ' },
    { text: notification.payload.roleName, emphasized: true },
    { text: '.' },
  ],
}

export default function getNotificationBody<T extends NotificationType>({
  type,
  notification,
}: {
  type: T
  notification: NotificationBodyArgs<T>
}): NotificationBodyDetails[] {
  return NOTIFICATION_TYPE_TO_BODY_DETAILS[type](notification)
}
